/**
 * Marking the controls not everybody has.
 *
 * The app has three roles and, until now, the only way to tell a control apart was to be denied
 * it. That is fine for a manager — he simply never sees the button — but it is wrong for the two
 * commissioners and for the admin, who spend their time on screens where some panels are shared
 * with the league and some are theirs alone, and who need to know which is which BEFORE they
 * change something in front of everyone.
 *
 * So privileged panels are tinted: pale yellow for the commissioners, pale purple for the admin.
 * The colour is a background wash rather than a border or a text colour, which keeps it clear of
 * the two channels already in use — saturated colour means STATUS (green eligible, amber warning,
 * red problem) and a pale circle means WHICH GM. A wash is a third channel that does not compete
 * with either.
 *
 * A colour nobody can decode is decoration, so every tinted panel also carries a small label
 * saying who it belongs to. Once you have read two of them the colour alone is enough.
 */

export type GateLevel = 'commish' | 'admin';

const LABEL: Record<GateLevel, string> = {
  commish: 'Commissioners',
  admin: 'Admin',
};

/** The corner label. Drop it in as the first child of a `.card.gated.<level>`. */
export function GateTag({ level, what }: { level: GateLevel; what?: string }) {
  return (
    <span className={`gatetag ${level}`}>
      <span className="ico" aria-hidden="true">{level === 'admin' ? '◆' : '★'}</span>
      {what ?? LABEL[level]}
    </span>
  );
}

/** A whole tinted panel, for the common case where the section exists only for that role. */
export function GatedCard(
  { level, what, className = '', children }:
  { level: GateLevel; what?: string; className?: string; children: React.ReactNode },
) {
  return (
    <section className={`card gated ${level} ${className}`}>
      <GateTag level={level} what={what} />
      {children}
    </section>
  );
}
